import { useEffect, useMemo, useState } from "react";
import { FloatButton, Image, Input, message } from "antd";

import { useLocation, useNavigate } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { AuthContextValue, useAuth } from "../../../providers/AuthProvider";
import {
  useUserUpdateStatus,
  useResetPassword,
  useLockUnlockUser,
} from "../../../api/users";
import CreateUserModal from "../../modals/CreateUserModal";
import { DataTable } from "../../ui/data-table";
import { User, UsersStatus } from "../../../types/user";
import { AppButton } from "../../ui/AppButton";
import { FilterTabs } from "../../ui/FilterTabs";
import { UserFormValues } from "../../../schemas/formSchema";
import { useCreateUser } from "../../../api/auth";
import { Icons } from "../../../assets/images/icons";
import { getStatusColor } from "../../../util/status-formatter";
import UserActionsMenu from "../../ui/UserActionsMenu";
import StatusUpdate from "../../modals/StatusUpdate";
import { ConfirmDialog } from "../../shared/ConfirmDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useTablePrint } from "../../../hooks/useTablePrint";
import { ExportButton } from "../../ui/ExportButton";

interface UsersTableProps {
  allUsers: any[];
  totalElements: number;
  isLoading: boolean;
  searchText: string;
  onSearchChange: (value: string) => void;
  activeFilter: string;
  onFilterChange: (value: string) => void;
  currentPage: number;
  onPageChange: (page: number, size: number) => void;
  pageSize: number;
  countAll?: number;
  filterCounts?: {
    all: number;
    active: number;
    inActive: number;
  };
}

const UsersTable = (props: UsersTableProps) => {
  const {
    allUsers,
    totalElements = 0,
    isLoading,
    searchText,
    onSearchChange,
    activeFilter,
    onFilterChange,
    currentPage,
    onPageChange,
    pageSize,
    countAll,
    filterCounts = {
      all: 0,
      active: 0,
      inActive: 0,
    },
  } = props;

  const location = useLocation().pathname.substring(1);
  const { user } = useAuth() as AuthContextValue;

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const handleCreate = () => {
    setIsDialogOpen(true);
  };

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmDescription, setConfirmDescription] = useState("");

  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [, setStatus] = useState<"ACTIVE" | "INACTIVE" | undefined>();
  const [, setIsMerchant] = useState(false);

  /** API */
  const { refetch: resetPassword } = useResetPassword(selectedId);
  const { refetch: lockUnlockUser } = useLockUnlockUser(selectedId);
  const { mutateAsync: updateStatus, isPending } = useUserUpdateStatus();
  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  const { handlePrint } = useTablePrint();

  /** Transform user list for table */
  const transformedUsers = useMemo((): User[] => {
    if (!allUsers) return [];

    return allUsers.map((item: User) => ({
      id: item.id,
      fullName: item.fullName || "N/A",
      email: item.email,
      username: item.username,
      phoneNo: item.phoneNo,
      role: item.role || "N/A",
      status: mapStatusToStandard(item.isActive), // for display only
      isLocked: item.isLocked,
    }));
  }, [allUsers]);

  const filterTabs = [
    { value: "all", label: "All", count: countAll },
    { value: "active", label: "Active", count: filterCounts.active },
    { value: "inActive", label: "Inactive", count: filterCounts.inActive },
  ];

  const usersColumns = [
    {
      key: "fullName",
      dataIndex: "fullName",
      title: "Full Name",
      render: (text: string, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            {record.avatar_url && (
              <Image
                src={record.avatar_url}
                style={{ width: 50, aspectRatio: 1, borderRadius: 50 }}
              />
            )}
          </div>
          <div>{record.fullName}</div>
        </div>
      ),
    },
    {
      key: "email",
      dataIndex: "email",
      title: "Email",
    },
    {
      key: "username",
      dataIndex: "username",
      title: "Username",
    },
    {
      key: "phoneNo",
      dataIndex: "phoneNo",
      title: "Phone Number",
    },
    {
      key: "role",
      dataIndex: "role",
      title: "Role",
    },
    {
      key: "isActive",
      dataIndex: "isActive",
      title: "Status",
      align: "left" as const,
      render: (_: any, record: any) => {
        return (
          <div
            className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
            style={{
              color: getStatusColor(record.status, "text"),
              backgroundColor: getStatusColor(record.status, "bg"),
            }}
          >
            {record.status.charAt(0).toUpperCase() +
              record.status.slice(1).toLowerCase()}
          </div>
        );
      },
    },
    {
      key: "actions",
      dataIndex: "actions",
      title: "Actions",
      render: (_: any, record: any) => {
        return (
          <UserActionsMenu
            record={record}
            onUpdateStatus={(rec) => {
              setSelectedId(rec.id);
              setStatus(rec.status === "ACTIVE" ? "INACTIVE" : "ACTIVE");
              setSelectedUser(rec);
              setOpen(true);
            }}
            onResetPassword={(rec) => {
              setSelectedId(rec.id);
              setConfirmTitle("Reset Password?");
              setConfirmDescription(
                "Are you sure you want to reset this user's password?"
              );
              setConfirmAction(() => resetUserPassword);
              setConfirmAction(() => () => resetUserPassword(rec.id));
              setConfirmOpen(true);
            }}
            onLockUnlock={(rec) => {
              setSelectedId(rec.id);
              setConfirmTitle(record.isLocked ? "Unlock User?" : "Lock User?");
              setConfirmDescription(
                record.isLocked
                  ? "This user is currently locked. Do you want to unlock them?"
                  : "This user is currently active. Do you want to lock them?"
              );
              setConfirmAction(() => () => _lockUnlockUser(rec.id));
              setConfirmOpen(true);
            }}
          />
        );
      },
    },
  ];

  const handleStatusUpdate = async (status: string) => {
    try {
      if (selectedId && status) {
        await updateStatus({
          id: Number(selectedId),
          status: status === "ACTIVE" ? 1 : 0,
        });
        message.success("User status updated successfully.");
      }
    } catch (err) {
      message.error("Operation failed");
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (user) {
      if (
        user.roleName === "ROLE_KFT_SUPER_ADMIN" &&
        location.indexOf("all-users") >= 0
      ) {
      }
    }
  }, [location, user]);

  const resetUserPassword = async (id: number) => {
    if (!id) return;

    try {
      const result = await resetPassword();

      setSelectedId("");

      if (result?.data?.data?.status === "success") {
        message.success("Password reset successfully.");
      } else {
        message.error("Operation failed.");
      }
    } catch (error) {
      setSelectedId("");
      message.error("Operation failed.");
    }
  };

  const _lockUnlockUser = async (id: number) => {
    if (!id) return;

    try {
      const result = await lockUnlockUser();
      setSelectedId("");

      const status = result?.data?.data?.status;
      const userData = result?.data?.data?.data;

      if (status === "success") {
        const msg =
          userData?.isLocked === 1
            ? "User locked successfully."
            : "User unlocked successfully.";
        message.success(msg);
        // manually refresh the user list
        queryClient.invalidateQueries({ queryKey: ["allUsers"] });
        queryClient.invalidateQueries({ queryKey: ["users"] });
      } else {
        message.error("Operation failed.");
      }
    } catch {
      setSelectedId("");
      message.error("Operation failed.");
    }
  };

  function mapStatusToStandard(
    status: string | number | undefined
  ): UsersStatus {
    if (status === undefined || status === null) return "INACTIVE";

    const normalized = String(status).trim().toUpperCase();

    if (["1", "ACTIVE", "TRUE"].includes(normalized)) {
      return "ACTIVE";
    }

    return "INACTIVE";
  }

  const handleRowClick = (record: User) => {
    if (location === "users") navigate(`/users/${record.id}`);
    else if (location === "all-users") navigate(`/all-users/${record.id}`);
  };

  const handleUserSubmit = async (data: UserFormValues): Promise<void> => {
    try {
      const payload = {
        orgId: user?.mfiId,
        fullName: data?.fullName,
        role:
          user?.roleName === "ROLE_SUPER_ADMIN"
            ? "WEB_SHEGAFRE_ADMIN"
            : data.role,
        username: data.userName,
        email: data.email,
        phoneNo: data.phoneNumber,
        bankAccount: data.bankAccount ? data.bankAccount : "N/A",
      };

      const result = await createUser(payload as any);
      if (result.data.status === "success") {
        message.success("Merchant user created successfully");
      } else {
        message.error("Operation failed");
      }

      setIsDialogOpen(false);
      setIsMerchant(false);
    } catch (err: any) {
      console.error(err);
      message.error("Operation failed");
    } finally {
      setIsDialogOpen(false);
    }
  };

  return (
    <div className="bg-transparent lg:bg-white">
      {/* Header with Filters */}
      <div className="space-y-4 mb-6">
        {/* Filter Tabs - Desktop */}
        <FilterTabs
          tabs={filterTabs}
          activeTab={activeFilter}
          onTabChange={onFilterChange}
          className="w-full lg:w-auto"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search users by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(usersColumns, transformedUsers, "User Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={usersColumns}
            data={transformedUsers}
            fileName="users"
            title="User Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />

          <CreateUserModal
            mode="create"
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            hideModal={() => setIsDialogOpen(false)}
            onSubmit={handleUserSubmit}
            isCreatingUser={isCreatingUser}
            trigger={
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={() => setIsDialogOpen(true)}
              >
                Create User
              </AppButton>
            }
          />
        </div>
      </div>

      <div className="hidden md:block">
        <DataTable<User>
          data={transformedUsers}
          columns={usersColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalElements ?? 0,
            onChange: (page, size) => onPageChange(page, size),
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No users found"
          rowKey="id"
        />
      </div>

      <div className="md:hidden w-full flex flex-col justify-between ">
        <div>
          <Input
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search"
            style={{ backgroundColor: "#08453E", color: "white", height: 40 }}
          />
        </div>

        {user?.roleName === "ROLE_KFT_SUPER_ADMIN" && (
          <FloatButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          />
        )}
      </div>

      {/* Status update modal */}
      <StatusUpdate
        open={open}
        onClose={() => setOpen(false)}
        onUpdate={handleStatusUpdate}
        isPending={isPending}
        currentStatus={selectedUser?.status}
      />

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={confirmTitle}
        description={confirmDescription}
        onConfirm={confirmAction}
      />
    </div>
  );
};

export default UsersTable;
